import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Building2, Mail, Lock, Phone, MapPin, Check, Shield, Calendar, Hash, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Session storage key for form data persistence
const FORM_STORAGE_KEY = 'lokalgrow_register_form';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const defaultRole = searchParams.get('role') || 'customer';
  
  // Try to restore form data from sessionStorage
  const getSavedFormData = () => {
    try {
      const saved = sessionStorage.getItem(FORM_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          email: parsed.email || '',
          password: '', // Don't restore password for security
          name: parsed.name || '',
          surname: parsed.surname || '',
          phone: parsed.phone || '',
          businessName: parsed.businessName || '',
          businessType: parsed.businessType || '',
          location: parsed.location || null,
          termsAccepted: false, // Don't restore terms acceptance
          postalCode: parsed.postalCode || '',
          birthDate: parsed.birthDate || ''
        };
      }
    } catch (e) {
      console.error('Error restoring form data:', e);
    }
    return null;
  };
  
  const [role, setRole] = useState(defaultRole);
  const [step, setStep] = useState(1); // 1: form, 2: phone verification
  const [formData, setFormData] = useState(() => getSavedFormData() || {
    email: '',
    password: '',
    name: '',
    surname: '',
    phone: '',
    businessName: '',
    businessType: '',
    location: null,
    termsAccepted: false,
    postalCode: '',
    birthDate: '',
    country: 'ES',  // Default Spain
    vatNumber: '',  // EU VAT number
    nif: ''         // Spanish NIF/CIF
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  // Save form data to sessionStorage whenever it changes
  useEffect(() => {
    const dataToSave = { ...formData };
    delete dataToSave.password; // Don't save password
    delete dataToSave.termsAccepted; // Don't save terms
    try {
      sessionStorage.setItem(FORM_STORAGE_KEY, JSON.stringify(dataToSave));
    } catch (e) {
      console.error('Error saving form data:', e);
    }
  }, [formData]);

  // Clear form data from sessionStorage after successful registration
  const clearSavedFormData = () => {
    try {
      sessionStorage.removeItem(FORM_STORAGE_KEY);
    } catch (e) {
      console.error('Error clearing form data:', e);
    }
  };

  const handleChange = (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const getLocation = () => {
    setGettingLocation(true);
    
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      setGettingLocation(false);
      toast.error('Tu navegador no soporta geolocalización');
      return;
    }
    
    // Check if we're on HTTPS (required for geolocation)
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setGettingLocation(false);
      toast.error('La geolocalización requiere una conexión segura (HTTPS)');
      return;
    }
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData({
          ...formData,
          location: {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
        });
        setGettingLocation(false);
        toast.success('Ubicación obtenida correctamente');
      },
      (error) => {
        console.error('Error getting location:', error);
        setGettingLocation(false);
        
        // Provide specific error messages based on error code
        switch(error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Permiso de ubicación denegado. Activa los permisos en la configuración de tu navegador.');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Ubicación no disponible. Verifica que el GPS esté activado.');
            break;
          case error.TIMEOUT:
            toast.error('Tiempo de espera agotado. Intenta de nuevo.');
            break;
          default:
            toast.error('No se pudo obtener la ubicación. Intenta de nuevo.');
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  const sendVerificationCode = async () => {
    if (!formData.phone) {
      toast.error('Ingresa tu número de móvil');
      return;
    }

    setSendingCode(true);
    try {
      const response = await axios.post(`${API}/verification/send-phone-code`, {
        phone: formData.phone
      });
      
      if (response.data.mock) {
        toast.info('Modo prueba: usa código 123456');
      } else {
        toast.success('Código enviado a tu móvil');
      }
      setCodeSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error enviando código');
    } finally {
      setSendingCode(false);
    }
  };

  const verifyCode = async () => {
    setLoading(true);
    try {
      const verifyResponse = await axios.post(`${API}/verification/verify-phone`, {
        phone: formData.phone,
        code: verificationCode
      });

      if (!verifyResponse.data.valid) {
        toast.error('Código incorrecto');
        setLoading(false);
        return;
      }

      // Now register the user
      await completeRegistration();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error verificando código');
      setLoading(false);
    }
  };

  const completeRegistration = async () => {
    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        surname: formData.surname || null,
        phone: formData.phone,
        role: role,
        location: formData.location,
        termsAccepted: formData.termsAccepted,
        postalCode: formData.postalCode,
        birthDate: formData.birthDate
      };

      // Debug log
      console.log('Registration payload:', JSON.stringify(payload));

      if (role === 'business') {
        payload.businessName = formData.businessName;
        payload.businessType = formData.businessType;
      }

      const response = await axios.post(`${API}/auth/register`, payload);

      if (response.data.success) {
        clearSavedFormData(); // Clear saved form data after successful registration
        toast.success('¡Registro exitoso! Inicia sesión para continuar');
        navigate('/login');
      }
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Error en el registro');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.termsAccepted) {
      toast.error('Debes aceptar los términos y condiciones');
      return;
    }

    if (!formData.phone) {
      toast.error('El número de móvil es obligatorio');
      return;
    }

    // Validate postal code before moving to step 2
    if (!formData.postalCode || formData.postalCode.trim().length < 4) {
      toast.error('El código postal es obligatorio');
      return;
    }

    // Validate birth date
    if (!formData.birthDate) {
      toast.error('La fecha de nacimiento es obligatoria');
      return;
    }

    // Log the form data for debugging
    console.log('Form data before step 2:', JSON.stringify(formData));

    setStep(2);
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-8 px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-6">
          <h1 className="font-manrope font-bold text-2xl sm:text-3xl mb-2">Únete a LokalGrow</h1>
          <p className="text-muted-foreground text-sm">Crea tu cuenta y komienza a ganar</p>
        </div>

        <AnimatePresence mode="wait">
          {step === 1 ? (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
            >
              {/* Role Selection */}
              <div className="flex gap-3 mb-6" data-testid="role-selection">
                <button
                  onClick={() => setRole('customer')}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                    role === 'customer'
                      ? 'border-local-sage bg-local-sage/10'
                      : 'border-border hover:border-local-sage/50'
                  }`}
                  data-testid="role-customer-button"
                >
                  <User className="w-5 h-5 mx-auto mb-1" />
                  <p className="font-semibold text-sm">Kliente</p>
                </button>
                <button
                  onClick={() => setRole('business')}
                  className={`flex-1 p-3 rounded-xl border-2 transition-all ${
                    role === 'business'
                      ? 'border-local-sage bg-local-sage/10'
                      : 'border-border hover:border-local-sage/50'
                  }`}
                  data-testid="role-business-button"
                >
                  <Building2 className="w-5 h-5 mx-auto mb-1" />
                  <p className="font-semibold text-sm">Negocio</p>
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="name" className="text-sm">Nombre</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleChange}
                      className="rounded-xl h-10"
                      placeholder="Juan"
                      required
                      data-testid="name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="surname" className="text-sm">Apellidos</Label>
                    <Input
                      id="surname"
                      name="surname"
                      type="text"
                      value={formData.surname}
                      onChange={handleChange}
                      className="rounded-xl h-10"
                      placeholder="Pérez"
                      data-testid="surname-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-sm">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 rounded-xl h-10"
                      placeholder="tu@email.com"
                      required
                      data-testid="email-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-sm">Móvil (obligatorio)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 rounded-xl h-10"
                      placeholder="+34 600 000 000"
                      required
                      data-testid="phone-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Te enviaremos un código de verificación</p>
                </div>

                <div>
                  <Label htmlFor="password" className="text-sm">Contraseña</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 rounded-xl h-10"
                      placeholder="••••••••"
                      required
                      minLength={6}
                      data-testid="password-input"
                    />
                  </div>
                </div>

                {role === 'business' && (
                  <>
                    <div>
                      <Label htmlFor="businessName" className="text-sm">Nombre del Negocio</Label>
                      <Input
                        id="businessName"
                        name="businessName"
                        type="text"
                        value={formData.businessName}
                        onChange={handleChange}
                        className="rounded-xl h-10"
                        placeholder="Mi Negocio Lokal"
                        required
                        data-testid="business-name-input"
                      />
                    </div>

                    <div>
                      <Label htmlFor="businessType" className="text-sm">Tipo de Negocio</Label>
                      <Input
                        id="businessType"
                        name="businessType"
                        type="text"
                        value={formData.businessType}
                        onChange={handleChange}
                        className="rounded-xl h-10"
                        placeholder="Restaurante, Tienda, etc."
                        required
                        data-testid="business-type-input"
                      />
                    </div>

                    {/* Country and Tax ID */}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label htmlFor="country" className="text-sm">País</Label>
                        <select
                          id="country"
                          name="country"
                          value={formData.country}
                          onChange={handleChange}
                          className="w-full h-10 px-3 bg-background border border-input rounded-xl text-sm"
                          data-testid="country-select"
                        >
                          <option value="ES">España</option>
                          <option value="FR">Francia</option>
                          <option value="DE">Alemania</option>
                          <option value="IT">Italia</option>
                          <option value="PT">Portugal</option>
                          <option value="NL">Países Bajos</option>
                          <option value="BE">Bélgica</option>
                          <option value="AT">Austria</option>
                          <option value="IE">Irlanda</option>
                          <option value="PL">Polonia</option>
                          <option value="GB">Reino Unido</option>
                          <option value="CH">Suiza</option>
                          <option value="US">Estados Unidos</option>
                          <option value="MX">México</option>
                          <option value="CO">Colombia</option>
                          <option value="AR">Argentina</option>
                          <option value="CL">Chile</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="nif" className="text-sm">
                          {formData.country === 'ES' ? 'NIF/CIF' : 'NIF-IVA (VAT)'}
                        </Label>
                        <Input
                          id={formData.country === 'ES' ? 'nif' : 'vatNumber'}
                          name={formData.country === 'ES' ? 'nif' : 'vatNumber'}
                          type="text"
                          value={formData.country === 'ES' ? formData.nif : formData.vatNumber}
                          onChange={handleChange}
                          className="rounded-xl h-10"
                          placeholder={formData.country === 'ES' ? 'B12345678' : 'FR12345678901'}
                          data-testid="tax-id-input"
                        />
                        {formData.country !== 'ES' && formData.country !== 'GB' && formData.country !== 'US' && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Con NIF-IVA válido: inversión del sujeto pasivo (0% IVA)
                          </p>
                        )}
                        {(formData.country === 'GB' || formData.country === 'US') && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Exportación de servicios: exento de IVA español
                          </p>
                        )}
                      </div>
                    </div>
                  </>
                )}

                {/* Location */}
                <div>
                  <Label className="text-sm">Ubicación</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={getLocation}
                    disabled={gettingLocation}
                    className="w-full rounded-xl h-10 text-sm"
                    data-testid="location-button"
                  >
                    <MapPin className="w-4 h-4 mr-2" />
                    {gettingLocation ? 'Obteniendo...' : formData.location ? 'Ubicación guardada ✓' : 'Obtener mi ubicación'}
                  </Button>
                  {!formData.location && (
                    <p className="text-xs text-muted-foreground mt-1">
                      Si no funciona, verifica que los permisos de ubicación estén activados en tu navegador
                    </p>
                  )}
                </div>

                {/* Postal Code and Birth Date - Required */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="postalCode" className="text-sm">Código Postal *</Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="postalCode"
                        name="postalCode"
                        type="text"
                        value={formData.postalCode}
                        onChange={handleChange}
                        className="pl-10 rounded-xl h-10"
                        placeholder="38001"
                        required
                        maxLength={10}
                        data-testid="postal-code-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="birthDate" className="text-sm">Fecha de nacimiento *</Label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="birthDate"
                        name="birthDate"
                        type="date"
                        value={formData.birthDate}
                        onChange={handleChange}
                        className="pl-10 rounded-xl h-10"
                        required
                        max={new Date(new Date().setFullYear(new Date().getFullYear() - 15)).toISOString().split('T')[0]}
                        data-testid="birth-date-input"
                      />
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">Debes tener al menos 15 años</p>
                  </div>
                </div>

                {/* Terms and Conditions */}
                <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-xl">
                  <input
                    type="checkbox"
                    id="termsAccepted"
                    name="termsAccepted"
                    checked={formData.termsAccepted}
                    onChange={handleChange}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                    data-testid="terms-checkbox"
                  />
                  <label htmlFor="termsAccepted" className="text-xs text-muted-foreground">
                    Acepto la{' '}
                    <Link to="/legal/privacy" className="text-local-sage hover:underline" target="_blank">
                      Política de Privacidad
                    </Link>
                    {' '}y los{' '}
                    <Link to="/legal/terms" className="text-local-sage hover:underline" target="_blank">
                      Términos y Condiciones
                    </Link>
                  </label>
                </div>

                <Button
                  type="submit"
                  disabled={loading || !formData.termsAccepted}
                  className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-5 mt-4"
                  data-testid="register-submit-button"
                >
                  Continuar
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  ¿Ya tienes cuenta?{' '}
                  <Link to="/login" className="text-local-sage hover:underline" data-testid="login-link">
                    Inicia Sesión
                  </Link>
                </p>
              </form>
            </motion.div>
          ) : (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* Phone Verification */}
              <div className="text-center">
                <div className="w-16 h-16 bg-local-sage/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-local-sage" />
                </div>
                <h2 className="font-semibold text-xl mb-2">Verifica tu móvil</h2>
                <p className="text-muted-foreground text-sm">
                  Te enviaremos un código SMS a<br />
                  <strong>{formData.phone}</strong>
                </p>
              </div>

              {!codeSent ? (
                <Button
                  onClick={sendVerificationCode}
                  disabled={sendingCode}
                  className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-5"
                  data-testid="send-code-button"
                >
                  {sendingCode ? 'Enviando...' : 'Enviar Código SMS'}
                </Button>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Código de Verificación</Label>
                    <Input
                      type="text"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      className="rounded-xl h-12 text-center text-2xl tracking-widest"
                      placeholder="000000"
                      maxLength={6}
                      data-testid="verification-code-input"
                    />
                  </div>

                  <Button
                    onClick={verifyCode}
                    disabled={loading || verificationCode.length < 6}
                    className="w-full bg-local-sage hover:bg-local-sage/90 rounded-full py-5"
                    data-testid="verify-code-button"
                  >
                    {loading ? 'Verificando...' : 'Verificar y Crear Cuenta'}
                  </Button>

                  <button
                    onClick={sendVerificationCode}
                    disabled={sendingCode}
                    className="w-full text-sm text-muted-foreground hover:text-local-sage"
                  >
                    ¿No recibiste el código? Reenviar
                  </button>
                </div>
              )}

              <button
                onClick={() => setStep(1)}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                ← Volver al formulario
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Prohibited Businesses Modal */}
    </div>
  );
};
